import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import type { BEER_LAMBERT_LAW_CALCULATOR_STRINGS } from "@/components/tools/beer-lambert-law-calculator";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  ABSORPTION_COEFFICIENT_UNITS,
  BEER_TERMS,
  type BeerSolution,
  type BeerTerm,
  CONCENTRATION_UNITS,
  convertFromSignal,
  PATH_LENGTH_UNITS,
  rangeStatus,
  readSignal,
  SIGNAL_UNITS,
  type Signal,
  solveBeer,
} from "@/lib/tool-kit/chem-physical/beer";
import {
  unitFactor,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

export type BeerStrings = typeof BEER_LAMBERT_LAW_CALCULATOR_STRINGS;

const DEFAULT_SIG = 3;
const SIGNAL_OPTIONS = SIGNAL_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const UNIT_LISTS = {
  e: ABSORPTION_COEFFICIENT_UNITS,
  l: PATH_LENGTH_UNITS,
  c: CONCENTRATION_UNITS,
} as const;

export function beerMessages(strings: BeerStrings): Record<string, string> {
  return {
    missing: strings.enterValues,
    unreadable: strings.errorUnreadable,
    nonPositive: strings.errorNonPositive,
    negativeAbsorbance: strings.errorNegativeAbsorbance,
    transmittanceRange: strings.errorTransmittanceRange,
    zeroAbsorbance: strings.errorZeroAbsorbance,
  };
}

function SignalCards({
  signal,
  sig,
  strings,
}: {
  signal: Signal;
  sig: number;
  strings: BeerStrings;
}) {
  const fmt = useToolNumber();
  return (
    <>
      <StatCard label={strings.termA} value={formatSig(fmt, signal.a, sig)} />
      <StatCard
        label={strings.percentT}
        value={`${formatSig(fmt, signal.pctT, sig)}%`}
      />
      <StatCard
        label={strings.absorbed}
        value={`${formatSig(fmt, signal.absorbedPct, sig)}%`}
      />
    </>
  );
}

function signalText(
  fmt: ReturnType<typeof useToolNumber>,
  value: number,
  unit: string,
  sig: number
): string {
  const shown = formatSig(fmt, value, sig);
  return unit === "pctT" ? `${shown}%` : shown;
}

function RangeNote({ a, strings }: { a: number; strings: BeerStrings }) {
  const status = rangeStatus(a);
  if (status === "good") {
    return null;
  }
  return (
    <Notice>{status === "low" ? strings.rangeLow : strings.rangeHigh}</Notice>
  );
}

function BeerAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: BeerSolution;
  texts: Record<BeerTerm, string>;
  units: { a: string; e: string; l: string; c: string };
  sig: number;
  strings: BeerStrings;
}) {
  const fmt = useToolNumber();
  const { unknown, signal } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const labels: Record<BeerTerm, string> = {
    a: strings.termA,
    e: strings.termE,
    l: strings.termL,
    c: strings.termC,
  };
  const answerText =
    unknown === "a"
      ? signalText(fmt, solution.answer, units.a, sig)
      : `${formatSig(fmt, solution.answer, sig)} ${unitSymbol(UNIT_LISTS[unknown], units[unknown])}`;
  const conversions = (["e", "l", "c"] as const)
    .filter(
      (term) =>
        term !== unknown && unitFactor(UNIT_LISTS[term], units[term]) !== 1
    )
    .map((term) =>
      t(strings.convertLine, {
        label: labels[term],
        value: texts[term].trim(),
        unit: unitSymbol(UNIT_LISTS[term], units[term]),
        converted: `${p(solution[term])} ${strings.baseUnits[term]}`,
      })
    );
  if (unknown !== "a" && units.a !== "A") {
    conversions.unshift(
      t(strings.convertSignal, {
        value: texts.a.trim(),
        unit: SIGNAL_UNITS.find((u) => u.id === units.a)?.symbol ?? "",
        a: p(signal.a),
      })
    );
  }
  const rearranged = {
    a: strings.equation,
    e: strings.rearrangedE,
    l: strings.rearrangedL,
    c: strings.rearrangedC,
  }[unknown];
  const substituted = t(
    {
      a: strings.substitutedA,
      e: strings.substitutedE,
      l: strings.substitutedL,
      c: strings.substitutedC,
    }[unknown],
    {
      a: p(signal.a),
      e: p(solution.e),
      l: p(solution.l),
      c: p(solution.c),
      result: formatSig(
        fmt,
        { a: signal.a, e: solution.e, l: solution.l, c: solution.c }[unknown],
        sig + 1
      ),
    }
  );
  const needsConversion =
    (unknown === "a" && units.a !== "A") ||
    (unknown !== "a" && unitFactor(UNIT_LISTS[unknown], units[unknown]) !== 1);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        {unknown === "a" ? (
          <>
            <StatCard
              label={strings.percentT}
              value={`${formatSig(fmt, signal.pctT, sig)}%`}
            />
            <StatCard
              label={strings.absorbed}
              value={`${formatSig(fmt, signal.absorbedPct, sig)}%`}
            />
          </>
        ) : (
          <SignalCards sig={sig} signal={signal} strings={strings} />
        )}
      </StatGrid>
      <RangeNote a={signal.a} strings={strings} />
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.equation}</p>
        {unknown === "a" ? null : <p>{rearranged}</p>}
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        {needsConversion && unknown !== "a" ? (
          <p>
            {t(strings.answerLine, {
              result: `${formatSig(fmt, solution[unknown], sig + 1)} ${strings.baseUnits[unknown]}`,
              answer: answerText,
            })}
          </p>
        ) : null}
        <p>
          {t(strings.transmittanceLine, {
            a: p(signal.a),
            pct: p(signal.pctT),
          })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

const EXAMPLES = [
  { id: "exampleNadh", e: "6220", l: "1", c: "100", cUnit: "uM" },
  { id: "exampleDye", e: "25000", l: "1", c: "20", cUnit: "uM" },
] as const;

export function BeerPanel({
  strings,
  sigChoice,
}: {
  strings: BeerStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<BeerTerm>("a");
  const [text, setText] = useState<Record<BeerTerm, string>>({
    a: "",
    e: "6220",
    l: "1.00",
    c: "100.",
  });
  const [unit, setUnit] = useState({ a: "A", e: "M", l: "cm", c: "uM" });
  const outcome = solveBeer({ unknown, text, unit });
  const typed = BEER_TERMS.filter((term) => term !== unknown).map(
    (term) => text[term]
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<BeerTerm, string> = {
    a: strings.termA,
    e: strings.termE,
    l: strings.termL,
    c: strings.termC,
  };
  const unitLabels = {
    a: strings.unitSignal,
    e: strings.unitCoefficient,
    l: strings.unitPath,
    c: strings.unitConcentration,
  };
  const unitList = {
    a: SIGNAL_OPTIONS,
    e: unitOptionsOf(ABSORPTION_COEFFICIENT_UNITS),
    l: unitOptionsOf(PATH_LENGTH_UNITS),
    c: unitOptionsOf(CONCENTRATION_UNITS),
  };
  const fill = (example: (typeof EXAMPLES)[number]) => {
    setUnknown("a");
    setUnit({ a: "A", e: "M", l: "cm", c: example.cUnit });
    setText({ a: "", e: example.e, l: example.l, c: example.c });
  };
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="bl-solve"
        onChange={setUnknown}
        options={BEER_TERMS.map((term) => ({
          value: term,
          label: labels[term],
        }))}
        value={unknown}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example.id}
            onClick={() => fill(example)}
            type="button"
          >
            {strings[example.id]}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {BEER_TERMS.map((term) => (
          <QuantityInput
            hint={term === "e" ? strings.coefficientHint : undefined}
            id={`bl-${term}`}
            key={term}
            label={
              unknown === term
                ? t(strings.answerSuffix, { label: labels[term] })
                : labels[term]
            }
            onUnitChange={(value) => setUnit({ ...unit, [term]: value })}
            onValueChange={(value) => setText({ ...text, [term]: value })}
            solved={unknown === term}
            unit={unit[term]}
            unitLabel={unitLabels[term]}
            units={unitList[term]}
            value={text[term]}
          />
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <BeerAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={{ ...labels, a: strings.termA }}
            messages={beerMessages(strings)}
          />
        )}
      </section>
    </>
  );
}

export function ConvertPanel({
  strings,
  sigChoice,
}: {
  strings: BeerStrings;
  sigChoice: string;
}) {
  const fmt = useToolNumber();
  const [text, setText] = useState("45.0");
  const [unit, setUnit] = useState("pctT");
  const read = readSignal(text, unit);
  const sig =
    sigChoice === "match"
      ? fewestSigFigs([text], DEFAULT_SIG)
      : Number(sigChoice);
  const p = (value: number) => formatPlain(fmt, value);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityInput
          id="bl-conv"
          label={strings.convertInput}
          onUnitChange={setUnit}
          onValueChange={setText}
          unit={unit}
          unitLabel={strings.unitSignal}
          units={SIGNAL_OPTIONS}
          value={text}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {"signal" in read ? (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.termA}
                value={formatSig(fmt, read.signal.a, sig)}
              />
              <StatCard
                label={strings.percentT}
                value={`${formatSig(fmt, read.signal.pctT, sig)}%`}
              />
              <StatCard
                label={strings.transmittanceFraction}
                value={formatSig(fmt, read.signal.t, sig)}
              />
              <StatCard
                label={strings.absorbed}
                value={`${formatSig(fmt, read.signal.absorbedPct, sig)}%`}
              />
            </StatGrid>
            <RangeNote a={read.signal.a} strings={strings} />
            <div className={WORKING_BOX}>
              <p className="font-medium font-sans text-foreground">
                {strings.working}
              </p>
              <p>{strings.convertEquation}</p>
              <p>
                {t(strings.convertWorking, {
                  a: p(read.signal.a),
                  pct: p(read.signal.pctT),
                  t: p(read.signal.t),
                })}
              </p>
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copy}
              text={t(strings.copyText, {
                label: strings.termA,
                value: formatSig(fmt, convertFromSignal(read.signal, "A"), sig),
              })}
            />
          </>
        ) : (
          <FailureNotice
            failure={read}
            labels={{ a: strings.convertInput }}
            messages={beerMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
